import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiConfig } from '@/utils/apiConfig'
import { useSettingsStore } from '@/stores/useSettingsStore'
import { isReactNativeWebView, nativePlatform, postToNative } from '@/utils/nativeBridge'
import {
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialRequestOptionsJSON,
  type PublicKeyCredentialCreationOptionsJSON,
  type AuthenticationResponseJSON,
  type RegistrationResponseJSON,
} from '@simplewebauthn/browser'

const TOKEN_KEY = 'wital_token'

// Tell the Expo shell about auth-state changes so it can (re-)register the
// APNs push token against the new user's JWT. INJECT_JS only posts the JWT
// on a full page load; in-WebView sign-in/out wouldn't otherwise reach the
// native side.
function notifyNativeAuthChanged(token: string | null) {
  if (!isReactNativeWebView()) return
  if (token) postToNative({ type: 'jwt', token })
  else postToNative({ type: 'logout' })
}

interface User {
  id: string
  email: string
  name: string
}

export type AuthMethod = 'passkey' | 'code'
export interface IdentifyResult {
  exists: boolean
  methods: AuthMethod[]
}

export interface PasskeyInfo {
  id: string
  nickname: string
  transports: string[]
  aaguid: string | null
  created_at: string | null
  last_used_at: string | null
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem(TOKEN_KEY))
  const user = ref<User | null>(null)
  const isLoggedIn = computed(() => !!token.value)

  // Global signal for components (e.g. the chat rate-limit CTA) that
  // want to open the sign-in dialog without owning it themselves. A
  // top-level <AuthScreen> dialog in App.vue watches this flag.
  const authDialogRequested = ref(false)
  function requestAuthDialog() { authDialogRequested.value = true }
  function dismissAuthDialog() { authDialogRequested.value = false }

  async function setSession(newToken: string, newUser: User) {
    token.value = newToken
    user.value = newUser
    localStorage.setItem(TOKEN_KEY, newToken)
    notifyNativeAuthChanged(newToken)
    // Sync settings: push local → server, then pull server → local
    const settings = useSettingsStore()
    await settings.pushToServer()
    await settings.pullFromServer()
  }

  function clearSession() {
    token.value = null
    user.value = null
    localStorage.removeItem(TOKEN_KEY)
    notifyNativeAuthChanged(null)
    useSettingsStore().clearDeepLinkLocation()
  }

  /**
   * Step 1 of identifier-first auth (#104, #129). Returns which credential
   * surfaces the UI should show for this email — `passkey` if a credential
   * is enrolled on any device, plus `code` (the universal email-OTP
   * fallback) for any existing account. `methods` is empty when the email
   * is unknown — UI then offers the OTP signup flow.
   */
  async function identify(email: string): Promise<IdentifyResult> {
    const resp = await fetch(apiConfig.buildUrl('/api/auth/identify'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(err.detail || 'Sign-in lookup failed')
    }
    return await resp.json()
  }

  /**
   * Email a 6-digit OTP for identifier-first signup or sign-in (#104).
   * Backend is existence-neutral — every call returns ok regardless of
   * whether an account exists. `purpose='signup'` will create a NULL-
   * password wital_user if one doesn't exist; `purpose='signin'` is a
   * no-op against unknown emails.
   */
  async function sendCode(email: string, purpose: 'signup' | 'signin' = 'signup'): Promise<void> {
    const resp = await fetch(apiConfig.buildUrl('/api/auth/code/send'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, purpose }),
    })
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(err.detail || 'Failed to send verification code')
    }
  }

  /**
   * Redeem a 6-digit OTP (#104). On success the backend mints a session
   * JWT, marks is_verified=TRUE, and we set the session. The OTP row is
   * deleted single-use.
   */
  async function verifyCode(email: string, code: string): Promise<void> {
    const resp = await fetch(apiConfig.buildUrl('/api/auth/code/verify'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    })
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(err.detail || 'This code is invalid or has expired')
    }
    const data = await resp.json()
    await setSession(data.token, data.user)
  }

  async function refresh(): Promise<boolean> {
    if (!token.value) return false
    try {
      // Check if token expires within 24 hours
      const payload = JSON.parse(atob(token.value.split('.')[1]))
      const secondsLeft = payload.exp - Math.floor(Date.now() / 1000)
      if (secondsLeft > 86400) return true // Still fresh

      const resp = await fetch(apiConfig.buildUrl('/api/auth/refresh'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token.value}` },
      })
      if (resp.ok) {
        const data = await resp.json()
        token.value = data.token
        localStorage.setItem(TOKEN_KEY, data.token)
        return true
      }
      clearSession()
      return false
    } catch {
      return true // Can't decode — leave as-is, backend will reject if invalid
    }
  }

  async function fetchProfile(): Promise<void> {
    if (!token.value) return
    try {
      const resp = await fetch(apiConfig.buildUrl('/api/auth/me'), {
        headers: { Authorization: `Bearer ${token.value}` },
      })
      if (resp.ok) {
        const data = await resp.json()
        user.value = { id: data.id, email: data.email, name: data.name }
        // Pull server settings for returning users
        const settings = useSettingsStore()
        await settings.pullFromServer()
      } else if (resp.status === 401) {
        clearSession()
      }
    } catch {
      // Silently fail — guest mode continues
    }
  }

  function logout() {
    clearSession()
  }

  async function deleteAccount(): Promise<void> {
    const resp = await fetch(apiConfig.buildUrl('/api/user'), {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token.value}` },
    })
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(err.detail || 'Account deletion failed')
    }
    clearSession()
  }

  // ── Native-bridge passkey ceremony (#161) ──────────────────────────────
  //
  // Android System WebView doesn't expose WebAuthn at all (Google gates it
  // to standalone Chrome by design). We route the JS ceremony through the
  // Expo shell's `react-native-passkeys` module instead — web does HTTP
  // (/begin + /finish), native runs CredentialManager.{getCredential,
  // createCredential}, response comes back via a `witalpasskey` CustomEvent.
  //
  // Only Android intercepts here. iOS WebView supports WebAuthn natively
  // (#126), and plain-browser users use the inline path unchanged. Phase 2
  // (#159) will move iOS to the same native bridge.
  function shouldUseNativePasskeyBridge(): boolean {
    return isReactNativeWebView() && nativePlatform() === 'android'
  }

  // One-shot RPC over the native bridge. Each call gets a unique id so
  // multiple in-flight ceremonies don't get crossed wires (rare, but the
  // conditional-mediation autofill path could collide with an explicit
  // tap mid-redirect). Resolves with the SimpleWebAuthn-shaped response
  // from the native module; rejects with an AbortError on user cancel
  // (CredentialManager wraps cancel as Cancelled / NotAllowed).
  function nextPasskeyId(): string {
    return `pk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  }

  function runNativePasskey<T>(
    type: 'passkey:get' | 'passkey:create',
    options: PublicKeyCredentialRequestOptionsJSON | PublicKeyCredentialCreationOptionsJSON,
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const id = nextPasskeyId()
      function onEvent(ev: Event) {
        const detail = (ev as CustomEvent<{
          id?: string
          ok?: boolean
          response?: T
          error?: { name?: string; message?: string }
        }>).detail || {}
        if (detail.id !== id) return
        window.removeEventListener('witalpasskey', onEvent as EventListener)
        if (detail.ok && detail.response) {
          resolve(detail.response)
          return
        }
        const errName = detail.error?.name || 'Error'
        const errMessage = detail.error?.message || 'Passkey ceremony failed'
        // Normalise CredentialManager's cancel codes to AbortError so
        // call-site `if (e.name === 'AbortError')` paths work uniformly.
        const cancelLike = ['Cancelled', 'NotAllowed', 'AbortError', 'NotAllowedError']
        const err = new Error(errMessage)
        err.name = cancelLike.includes(errName) ? 'AbortError' : errName
        reject(err)
      }
      window.addEventListener('witalpasskey', onEvent as EventListener)
      postToNative({ type, id, options })
    })
  }

  /**
   * Passkey login. Calls /auth/passkey/login/begin to fetch a server-issued
   * challenge, hands it to @simplewebauthn/browser's startAuthentication,
   * then posts the assertion to /auth/passkey/login/finish to mint a
   * session JWT.
   *
   * `email` is optional: with it, allowCredentials hints the picker at
   * one specific account; without it, the browser shows the full
   * passkey picker (resident-credential / discoverable flow). Pass
   * `mediation: 'conditional'` to drive the call from inline autofill
   * — the AuthScreen onMounted hook does this so users don't need to
   * tap a separate "passkey" button. The library's WebAuthnAbortService
   * tracks the in-flight ceremony; callers wanting to cancel (e.g. on
   * dialog close) call WebAuthnAbortService.cancelCeremony().
   *
   * Throws on failure; resolves on success after the session is set.
   * Caller should treat AbortError as "user picked password instead"
   * and not surface it as an error.
   */
  async function loginWithPasskey(opts: {
    email?: string
    mediation?: CredentialMediationRequirement
  } = {}): Promise<void> {
    const useNative = shouldUseNativePasskeyBridge()
    // Conditional-mediation autofill is a browser-only construct — the
    // native Credential Manager path doesn't surface autofill from JS,
    // so silently no-op. Explicit ceremonies route through the bridge.
    if (useNative && opts.mediation === 'conditional') return

    const beginResp = await fetch(apiConfig.buildUrl('/api/auth/passkey/login/begin'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: opts.email }),
    })
    if (!beginResp.ok) {
      const err = await beginResp.json().catch(() => ({}))
      throw new Error(err.detail || 'Passkey sign-in failed')
    }
    const begin = await beginResp.json()
    const credential = useNative
      ? await runNativePasskey<AuthenticationResponseJSON>('passkey:get', begin.options)
      : await startAuthentication({
          optionsJSON: begin.options,
          useBrowserAutofill: opts.mediation === 'conditional',
        })

    const finishResp = await fetch(apiConfig.buildUrl('/api/auth/passkey/login/finish'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket: begin.ticket, credential }),
    })
    if (!finishResp.ok) {
      const err = await finishResp.json().catch(() => ({}))
      throw new Error(err.detail || 'Passkey sign-in failed')
    }
    const data = await finishResp.json()
    await setSession(data.token, data.user)
  }

  /**
   * Register a passkey for the currently signed-in user. The user picks
   * a nickname (e.g. "iPhone 15") so the Settings list can render a
   * meaningful label instead of an opaque hex blob.
   */
  async function registerPasskey(nickname: string): Promise<void> {
    if (!token.value) throw new Error('Not signed in')
    const useNative = shouldUseNativePasskeyBridge()

    const beginResp = await fetch(apiConfig.buildUrl('/api/auth/passkey/register/begin'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token.value}` },
    })
    if (!beginResp.ok) {
      const err = await beginResp.json().catch(() => ({}))
      throw new Error(err.detail || 'Passkey registration failed')
    }
    const begin = await beginResp.json()
    const credential = useNative
      ? await runNativePasskey<RegistrationResponseJSON>('passkey:create', begin.options)
      : await startRegistration({ optionsJSON: begin.options })

    const finishResp = await fetch(apiConfig.buildUrl('/api/auth/passkey/register/finish'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token.value}`,
      },
      body: JSON.stringify({
        ticket: begin.ticket,
        nickname,
        credential,
      }),
    })
    if (!finishResp.ok) {
      const err = await finishResp.json().catch(() => ({}))
      throw new Error(err.detail || 'Passkey registration failed')
    }
  }

  async function listPasskeys(): Promise<PasskeyInfo[]> {
    if (!token.value) return []
    const resp = await fetch(apiConfig.buildUrl('/api/auth/passkey/list'), {
      headers: { Authorization: `Bearer ${token.value}` },
    })
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(err.detail || 'Failed to load passkeys')
    }
    const data = await resp.json()
    return data.credentials as PasskeyInfo[]
  }

  async function deletePasskey(credentialId: string): Promise<void> {
    if (!token.value) throw new Error('Not signed in')
    const resp = await fetch(
      apiConfig.buildUrl(`/api/auth/passkey/${encodeURIComponent(credentialId)}`),
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token.value}` },
      },
    )
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      throw new Error(err.detail || 'Failed to remove passkey')
    }
  }

  return {
    token,
    user,
    isLoggedIn,
    authDialogRequested,
    requestAuthDialog,
    dismissAuthDialog,
    setSession,
    identify,
    sendCode,
    verifyCode,
    loginWithPasskey,
    registerPasskey,
    listPasskeys,
    deletePasskey,
    refresh,
    fetchProfile,
    logout,
    deleteAccount,
  }
})
